import { notification } from 'antd'
import i18next from 'i18next'
import { action, computed, makeAutoObservable, observable } from 'mobx'
import { captureException } from '@sentry/react'

import { FileEntityType, fileService } from 'services/FileService'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { VideoFile } from 'sections/LKSection/modules/OrderModule/types'
import { Pvz } from 'sections/LKSection/modules/PvzModule/stores/entity/Pvz'
import {
  PickPointFileItemType,
  PickPointFileType,
  PickPointVideoItemType,
} from 'sections/LKSection/shared/PickPointSharedModule/types'
import { getActualUrls } from 'sections/LKSection/shared/PickPointSharedModule/utils'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { WEB_LINK_REGEXP } from 'shared/constants/regExp'

import { defaultFileData, MIN_PUBLIC_PHOTO } from './../data'

export class PickPointFilesViewModel {
  /** Фото фасада с вывеской */
  @observable generalPhoto: PickPointFileItemType = { ...defaultFileData, type: PickPointFileType.generalPhoto }
  /** Ночное фото фасада */
  @observable nightlyGeneralPhoto: PickPointFileItemType = {
    ...defaultFileData,
    type: PickPointFileType.nightlyGeneralPhoto,
  }
  /** Фото клиентской зоны */
  @observable clientAreaPhoto: PickPointFileItemType = { ...defaultFileData, type: PickPointFileType.clientAreaPhoto }
  /** Фото примерочной */
  @observable fittingRoomsPhoto: PickPointFileItemType = {
    ...defaultFileData,
    type: PickPointFileType.fittingRoomsPhoto,
  }
  /** Фото складской зоны */
  @observable warehouseAreaPhoto: PickPointFileItemType = {
    ...defaultFileData,
    type: PickPointFileType.warehouseAreaPhoto,
  }
  /** Фото видеонаблюдения */
  @observable surveillancePhoto: PickPointFileItemType = {
    ...defaultFileData,
    type: PickPointFileType.surveillancePhoto,
  }
  /** Фото кронштейна */
  @observable bracketPhoto: PickPointFileItemType = { ...defaultFileData, type: PickPointFileType.bracketPhoto }

  /** Документ аренды */
  @observable leaseDocument: PickPointFileItemType = { ...defaultFileData, type: PickPointFileType.leaseDocument }
  /** Документ БТИ */
  @observable btiDocument: PickPointFileItemType = { ...defaultFileData, type: PickPointFileType.btiDocument }

  /** Документ аренды используемый при редактировании площади ПВЗ */
  @observable leaseDocumentEditArea: PickPointFileItemType = {
    ...defaultFileData,
    type: PickPointFileType.leaseDocumentEditArea,
  }
  /** Документ БТИ используемый при редактировании площади ПВЗ */
  @observable btiDocumentEditArea: PickPointFileItemType = {
    ...defaultFileData,
    type: PickPointFileType.btiDocumentEditArea,
  }

  /** Видеопрогулка */
  @observable video: VideoFile = { url: '', name: '', size: 0 }
  /** Ссылка на видеопрогулку */
  @observable videoURL: string = ''

  constructor() {
    makeAutoObservable(this)
    this.reset()
  }

  @computed
  get photos(): PickPointFileItemType[] {
    return [
      this.generalPhoto,
      this.nightlyGeneralPhoto,
      this.clientAreaPhoto,
      this.fittingRoomsPhoto,
      this.warehouseAreaPhoto,
      this.surveillancePhoto,
      this.bracketPhoto,
    ]
  }

  @computed
  get documents(): PickPointFileItemType[] {
    return [this.leaseDocument, this.btiDocument]
  }

  @computed
  get documentsForEditArea(): PickPointFileItemType[] {
    return [this.leaseDocumentEditArea, this.btiDocumentEditArea]
  }

  @computed
  get videos(): VideoFile[] {
    return [this.video, { name: '', size: 0, url: this.videoURL || '' }]
  }

  @computed
  invalidPhotos(actionType: 'create' | 'activate') {
    const photosForCreation = [
      this.generalPhoto,
      this.nightlyGeneralPhoto,
      this.clientAreaPhoto,
      this.fittingRoomsPhoto,
    ]
    const photosForActivation = [
      ...photosForCreation,
      this.warehouseAreaPhoto,
      this.surveillancePhoto,
      this.bracketPhoto,
    ]
    const photos = actionType === 'create' ? photosForCreation : photosForActivation

    return photos.some((photo) => !photo?.url?.length)
  }

  @computed
  get invalidPublicPhotos() {
    return this.photos.filter((photo) => photo.isPublic && photo.url).length < MIN_PUBLIC_PHOTO
  }

  @computed
  get invalidDocuments() {
    return this.documents.some((doc) => doc?.url === '')
  }

  @computed
  get invalidVideo() {
    return !(this.videos[0]?.url || this.videos[1]?.url)
  }

  @computed
  get invalidVideoCount() {
    // если загружена видепрогулка и ссылка на нее - ошибка
    return Boolean(this.videos[0]?.url && this.videos[1]?.url)
  }

  @computed
  get invalidVideoUrl() {
    return this.videoURL && !WEB_LINK_REGEXP.test(this.videoURL)
  }

  @action
  setPhotoData = (key: PickPointFileType) =>
    action((item: Omit<PickPointFileItemType, 'isPublic' | 'type'>) => {
      if (key !== PickPointFileType.video) {
        this[key].url = item.url
        this[key].size = item.size
        this[key].type = key
      }
    })

  @action
  setPhotoIsPublic = (key: PickPointFileType) =>
    action((value: boolean) => {
      if (key !== PickPointFileType.video) {
        this[key].isPublic = value
      }
    })

  @action
  setVideoFile = (value: VideoFile) => {
    this.video = value
  }

  @action
  setVideoURL = (value: string) => {
    this.videoURL = value.replace(/\s/g, '')
  }

  @action
  setPhotos = (photos: PickPointFileItemType[]) => {
    for (const photo of photos) {
      if (this[photo.type]) {
        const typedPhoto = this[photo.type] as PickPointFileItemType

        typedPhoto.url = photo.url
        typedPhoto.isPublic = photo.isPublic
      }
    }
  }

  async processFiles(entity: Order | Pvz) {
    if (!entity.id) {
      return false
    }

    const isOrder = entity instanceof Order
    const entityType = isOrder ? FileEntityType.pickPointOrder : FileEntityType.pickPoint

    const oldVideos: PickPointVideoItemType[] = []
    const oldPhotos: PickPointFileItemType[] = []
    const oldDocuments: PickPointFileItemType[] = []

    const newVideos: VideoFile[] = []
    const newPhotos: PickPointFileItemType[] = []
    const newDocuments: PickPointFileItemType[] = []

    for (const item of this.videos ?? []) {
      if (entity.videos?.some((video) => video.url === item.url)) {
        oldVideos.push(item)
      } else {
        item.url && newVideos.push(item)
      }
    }
    for (const item of this.photos ?? []) {
      if (entity.photos?.some((x) => x.url === item.url)) {
        oldPhotos.push(item)
      } else {
        item.url && newPhotos.push(item)
      }
    }
    for (const item of [...this.documents, ...this.documentsForEditArea]) {
      if (entity.documents?.some((x) => x.url === item.url)) {
        oldDocuments.push(item)
      } else {
        item.url && newDocuments.push(item)
      }
    }

    try {
      const [savedVideos, savedPhotos, savedDocuments] = await Promise.all([
        this.uploadVideo(entity.id, newVideos, entityType),
        this.uploadPhotos(entity.id, newPhotos, entityType),
        this.uploadPhotos(entity.id, newDocuments, entityType, true),
      ])

      const actualUrls =
        getActualUrls(
          oldPhotos.concat(savedPhotos),
          oldVideos.concat(savedVideos),
          oldDocuments.concat(savedDocuments),
        ) || false

      return actualUrls
    } catch (error) {
      captureException(
        `Ошибка загрузки файлов для ${isOrder ? 'заявки' : 'ПВЗ'} № ${entity.id}: ${(error as Error).message}`,
      )
      notification.error({ message: (error as Error).message })
      return false
    }
  }

  @action
  async uploadPhotos(
    id: number,
    photos: PickPointFileItemType[],
    entityType: FileEntityType,
    isDocument: boolean = false,
  ): Promise<PickPointFileItemType[]> {
    try {
      const res = await fileService.filesUpload({
        entityId: id,
        entityType: isDocument ? FileEntityType.document : entityType,
        files: photos.map((photo) => ({ content: photo.url, totalSize: photo.size })),
      })

      if (res.length < photos.length) {
        throw new Error(i18next.t(getSharedKey(isDocument ? 'api.documentsUploadError' : 'api.photosUploadError')))
      }

      return res.map((photo, index) => ({
        size: photo.uploadedSize,
        url: photo.imageUrl,
        type: photos[index].type,
        isPublic: photos[index].isPublic,
      }))
    } catch (error) {
      throw new Error(
        `${i18next.t(getSharedKey(isDocument ? 'api.documentsUploadError' : 'api.photosUploadError'))}. ${
          (error as Error).message
        }`,
      )
    }
  }

  @action
  async uploadVideo(id: number, videos: VideoFile[], entityType: FileEntityType): Promise<VideoFile[]> {
    try {
      const res = await fileService.filesUpload({
        entityId: id,
        entityType,
        files: videos.map((video) => ({ content: video.url, totalSize: video.size })),
      })

      if (res.length < videos.length) {
        throw new Error(i18next.t(getSharedKey('api.videoUploadError')))
      }

      return res.map((video, index) => ({ size: video.uploadedSize, url: video.imageUrl, name: videos[index].name }))
    } catch (error) {
      throw new Error(`${i18next.t(getSharedKey('api.videoUploadError'))}. ${(error as Error).message}`)
    }
  }

  @action
  reset = () => {
    const pickPointFileTypeKeys = Object.keys(PickPointFileType)

    for (const key of pickPointFileTypeKeys) {
      const typedKey = key as PickPointFileType

      if (typedKey !== PickPointFileType.video) {
        this[typedKey] = { ...defaultFileData, type: typedKey }
      }
    }

    this.video = { url: '', name: '', size: 0 }
    this.videoURL = ''
  }
}
